/**
 *
 * @module adobeIdentityManager
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Services from './../../services/entry';
import Token from './../../token/entry';
import AdobeRegistration from './adobeRegistration';

const AccessTokenProvider = Token.AccessTokenProvider;
const AdobeIdentityManagerConfiguration = Services.Configuration.AdobeIdentityManagerConfiguration;
const AdobeIdentityClient = Services.Identity.Adobe.AdobeIdentityClient;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class AdobeIdentityManager
 *
 */
export default class AdobeIdentityManager {

    /**
     *
     * @param {SDK.Services.Configuration.AdobeIdentityManagerConfiguration} adobeIdentityManagerConfiguration
     * @param {SDK.Services.Identity.Adobe.AdobeIdentityClient} adobeIdentityClient
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Token.AccessTokenProvider} accessTokenProvider
     *
     */
    constructor(adobeIdentityManagerConfiguration, adobeIdentityClient, logger = Logging, accessTokenProvider) {

        const errorMessage = {
            ctor: 'AdobeIdentityManager(adobeIdentityManagerConfiguration, adobeIdentityClient, logger, accessTokenProvider)', // eslint-disable-line
            get adobeIdentityManagerConfiguration() {
                return `${this.ctor} needs SDK.Services.Configuration.AdobeIdentityManagerConfiguration instance`;
            },
            get adobeIdentityClient() {
                return `${this.ctor} needs SDK.Services.AdobeIdentityClient instance`;
            },
            get accessTokenProvider() {
                return `${this.ctor} needs SDK.Token.AccessTokenProvider instance`;
            }
        };

        instanceStrict(adobeIdentityManagerConfiguration, AdobeIdentityManagerConfiguration, adobeIdentityManagerConfiguration, InvalidArgumentException); // eslint-disable-line
        instanceStrict(adobeIdentityClient, AdobeIdentityClient, errorMessage.adobeIdentityClient, InvalidArgumentException); // eslint-disable-line
        instanceStrict(accessTokenProvider, AccessTokenProvider, errorMessage.accessTokenProvider, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.AdobeIdentityManagerConfiguration}
         *
         */
        this.config = adobeIdentityManagerConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Services.AdobeIdentityClient}
         *
         */
        this.client = adobeIdentityClient;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Token.AccessTokenProvider}
         *
         */
        this.accessTokenProvider = accessTokenProvider;

        this.logger.log(AdobeIdentityManager.name, 'Created');

    }

    /**
     *
     * @access public
     * @desc Gets an AdobeRegistration instance with a code that can be
     * used to activate the identity.
     * @returns {Promise<AdobeRegistration>} Returns an AdobeRegistration that can
     * be passed to AdobeIdentityApi#activateRegistration to activate the identity.
     *
     */
    getRegistration() {

        return new Promise((resolve, reject) => {
            return reject(new Error('Not Implemented'));
        });

    }

    /**
     *
     * @access public
     * @param {AdobeRegistration} adobeRegistration - The AdobeRegistration that
     * was generated for the identity.
     * @param {String} adobeId - The ID of the MVPD the identity is activating with.
     * @desc Activates the Adobe identity.
     * @returns {Promise<AdobeActivationResult>} Returns an asynchronous action that will
     * send a notification when it is complete
     *
     */
    activateRegistration(adobeRegistration, adobeId) {

        const errorMessage = {
            method: 'AdobeIdentityManager.activateRegistration(adobeRegistration, adobeId)',
            get adobeRegistration() {
                return `${this.method} needs AdobeRegistration instance`;
            },
            get adobeId() {
                return `${this.method} adobeId should be a String with length`;
            }
        };

        instanceStrict(adobeRegistration, AdobeRegistration, errorMessage.adobeRegistration, InvalidArgumentException);
        nonEmptyString(adobeId, errorMessage.adobeId, InvalidArgumentException);

        return new Promise((resolve, reject) => {
            return reject(new Error('Not Implemented'));
        });

    }

}



// WEBPACK FOOTER //
// ./src/identity/adobe/adobeIdentityManager.js